import React, { useEffect, useState } from "react";
import Tabs from "components/lib/Shared/Tabs";
import SignatureDocument from "./SignatureDocument";
import { useSelector } from "react-redux";
import AuditTrail from "./AuditTrail";
import { id } from "date-fns/locale";

/**
 * Helper to get the correct document from approvalDocs by key
 * @param {Array} approvalDocs - Array of approval document objects
 * @param {string} key - The key to look for
 * @returns {any} - The document data or undefined
 */
const getApprovalDoc = (approvalDocs, key) => {
  const docObj = approvalDocs.find((doc) => Object.keys(doc)[0] === key);
  return docObj ? docObj[key] : undefined;
};

/**
 * ESignature component handles the display and logic for various signature document flows
 * (director, shareholder, court order, gazette, creditors) and audit trail integration.
 *
 * Props:
 * - officialIndex: Index of the official (if applicable)
 * - actionType: Type of action (signing, etc.)
 * - documents: Document data
 * - isViewMode: Boolean for view-only mode
 */
const ESignature = (props) => {
  // Redux selectors for transaction and approval data
  const { transactionUnderReview } = useSelector(
    (state) => state.transactionSlice
  );
  const { transactions, unsignedApprovalFileUploads, approvalDocs } =
    transactionUnderReview.items;
  const fileUploads = unsignedApprovalFileUploads;
  const { officialIndex, actionType } = props;

  const approvalDocsHasConvertedPdf = () => {
    return approvalDocs.some((approvalDoc) => approvalDoc?.convertedPdf);
  };

  // Memoized tab definitions based on transaction types
  const initialTabs = React.useMemo(() => {
    if (!transactions.length) return [];
    let tabs = [];
    if (approvalDocsHasConvertedPdf()) {
      tabs = approvalDocs
        .filter(
          (doc) =>
            Object.keys(doc)[0] === "directorApprovalDocs" ||
            Object.keys(doc)[0] === "shareholderApprovalDocs"
        )
        .map((doc) => {
          const key = Object.keys(doc)[0];
          return {
            id: key,
            label: key.split(/(?=[A-Z])/).join(" "), // Convert camelCase to spaced words
          };
        });
    }

    tabs.push({
      id: "auditTrail",
      label: "Audit Trail",
    });
    return tabs;
  }, [transactions]);

  // State for tabs and selection
  const [tabs, setTabs] = useState(initialTabs);
  const [selectedTab, setSelectedTab] = useState(tabs[0]);
  const [selectedTabIndex, setSelectedTabIndex] = useState(0);

  // Update selected tab when index changes
  useEffect(() => {
    setSelectedTab(tabs[selectedTabIndex]);
  }, [selectedTabIndex, tabs]);

  // Render the correct document component based on the selected tab
  const renderTabContent = () => {
    switch (selectedTab?.id) {
      case "auditTrail":
        return <AuditTrail />;
      case "directorApprovalDocs":
        return (
          <SignatureDocument
            isViewMode={props?.isViewMode}
            signedDocumentType={"directorApprovalSignedDocuments"}
            selectedTab="Director"
            fileUploads={fileUploads}
            document={getApprovalDoc(approvalDocs, "directorApprovalDocs")}
            documentType="directorApprovalDocs"
            actionType={actionType}
          />
        );
      case "shareholderApprovalDocs":
        return (
          <SignatureDocument
            isViewMode={props?.isViewMode}
            signedDocumentType={"shareholderApprovalSignedDocuments"}
            officialIndex={officialIndex}
            selectedTab="Shareholder"
            document={getApprovalDoc(approvalDocs, "shareholderApprovalDocs")}
            documentType="shareholderApprovalDocs"
            actionType={actionType}
            fileUploads={fileUploads}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div>
      {/* Tab navigation */}
      <Tabs
        tabs={initialTabs}
        selectedTabIndex={selectedTabIndex}
        setSelectedTabIndex={setSelectedTabIndex}
      />
      <div>{renderTabContent()}</div>
    </div>
  );
};

export default ESignature;
